package com.swetdot.swetgram.protocol.dto

import com.swetdot.swetgram.protocol.ResourceVersion
import com.swetdot.swetgram.protocol.Timestamp
import com.swetdot.swetgram.protocol.Uuid
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/*
 * Profile, privacy, blocking and presence wire types.
 *
 * `ChangePasswordRequest` for `changePassword` is NOT here -- it already lives in `Auth.kt`
 * alongside the other credential requests, and its shape (`current_password` + `new_password`)
 * already matches the contract. Do not add a second one.
 */

// ------------------------------------------------------------------ profile (updateMe)

/**
 * `UpdateProfileRequest` -- `PATCH /users/me`, operationId `updateMe`.
 *
 * [version] is the ONLY required property, and therefore the only one without a default. That
 * matters more here than anywhere else in this module: `version` is the optimistic-concurrency
 * token the contract checks to answer `409 version_conflict`. Given a Kotlin default, a client
 * that forgot to send one would ship a plausible-looking number, the check would pass against
 * some other revision, and one of two devices editing the same profile would silently lose its
 * write. Absent must therefore be a decode failure, which is the ordinary 400.
 *
 * The other four are optional; the contract's `minProperties: 2` means at least one of them must
 * accompany the version, so a request built with none is a guaranteed 400 -- see [hasChanges].
 *
 * CLEARING A FIELD IS NOT EXPRESSIBLE HERE, deliberately. The contract types `bio` and
 * `avatar_media_id` as nullable, where an explicit JSON `null` means "clear this" as opposed to
 * an absent key meaning "leave unchanged". `ProtocolJson` sets `explicitNulls = false`, so a
 * Kotlin `null` is OMITTED rather than written as `null` -- which is exactly right for "leave
 * unchanged" and cannot produce "clear". The reverse direction is lossy in the same way: for a
 * nullable property, kotlinx hands back `null` for both an absent key and an explicit `null`, so
 * no field arrangement here could carry the distinction in both directions.
 *
 * So the two ends handle it outside this type. A client that removes an avatar or empties a bio
 * builds the body as a `JsonObject` with `JsonNull` in that key, and the SERVER decodes into its
 * own view over the raw object -- where absent, `null` and a value are three distinct states --
 * and merges. Do not "fix" this by adding a wrapper type: the shape above is the one the wire
 * defines, and a client passing a plain value must not have to know about the null case.
 */
@Serializable
data class UpdateProfileRequest(
    /** Required. The version last read from `UserPrivate`; a stale one answers 409. */
    @SerialName("version") val version: ResourceVersion,
    /** `minLength: 1`, `maxLength: 80` -- [com.swetdot.swetgram.protocol.Constraints]. */
    @SerialName("display_name") val displayName: String? = null,
    /**
     * Lowercase, 3..24 of `[a-z0-9_]`, not a reserved name. Changeable once every 30 days; the
     * next permitted time comes back as `UserPrivate.username_change_available_at`.
     */
    @SerialName("username") val username: String? = null,
    /** `maxLength: 160`. Null here means "leave unchanged", NOT "clear" -- see the class doc. */
    @SerialName("bio") val bio: String? = null,
    /** ISO 8601 date string (YYYY-MM-DD). Null means "leave unchanged". */
    @SerialName("date_of_birth") val dateOfBirth: String? = null,
    /** Whether to show birth year. Null means "leave unchanged". */
    @SerialName("show_birth_year") val showBirthYear: Boolean? = null,
    /** User location string. Null means "leave unchanged". */
    @SerialName("location") val location: String? = null,
    /** Resolve the current session's server-provided location hint. Only `true` is meaningful. */
    @SerialName("use_location_hint") val useLocationHint: Boolean? = null,
    /** An id from a completed upload. Null means "leave unchanged", NOT "remove the avatar". */
    @SerialName("avatar_media_id") val avatarMediaId: Uuid? = null,
) {
    /** Mirrors the contract's `minProperties: 2`, so an empty edit can be caught before a trip. */
    val hasChanges: Boolean
        get() = displayName != null || username != null || bio != null ||
            dateOfBirth != null || showBirthYear != null || location != null ||
            useLocationHint == true ||
            avatarMediaId != null
}

// ------------------------------------------------------------------------------ privacy

/**
 * Audience for the two presence fields: `show_online` and `show_last_seen`.
 *
 * [SHARED_GROUPS] is the middle setting -- visible to people who share a group with the user --
 * and it is spelled `shared_groups` on the wire, not `contacts`: this app has no contact list.
 */
@Serializable
enum class PresenceAudience {
    @SerialName("everyone") EVERYONE,
    @SerialName("shared_groups") SHARED_GROUPS,
    @SerialName("nobody") NOBODY,
    @SerialName("unknown") UNKNOWN,
}

/**
 * Audience for `allow_messages_from`. Note the third member differs from [PresenceAudience]:
 * the contract's enum here is `everyone | shared_groups | existing_chats`, with NO `nobody`.
 * Someone the user is already talking to can always keep talking to them.
 */
@Serializable
enum class MessageAudience {
    @SerialName("everyone") EVERYONE,
    @SerialName("shared_groups") SHARED_GROUPS,
    @SerialName("existing_chats") EXISTING_CHATS,
    @SerialName("unknown") UNKNOWN,
}

/**
 * `PrivacySettings` -- `GET`/`PATCH /users/me/privacy`.
 *
 * All four properties are `required` in the contract, and `PrivacySettingsUpdate` is defined as
 * `allOf: [PrivacySettings]` -- the identical schema -- so a conforming PATCH carries all four.
 * None of them may have a Kotlin default. This is the same reasoning as `NotificationSettings`
 * but with sharper consequences: a default would let a body that omitted `show_last_seen` decode
 * as `everyone` and republish a last-seen timestamp the user had just hidden, answering 200 with
 * the setting they were trying to turn off.
 *
 * Because the enum properties have no default there is nothing for `coerceInputValues` to coerce
 * to, so an unrecognised member is a decode failure rather than [PresenceAudience.UNKNOWN]. That
 * is the safe direction for a privacy control in both directions of travel: an unknown value
 * stored as "unknown" would later have to be interpreted by the visibility check, and the only
 * two readings are "show it" (leaks) or "hide it" (silently stricter than the user chose).
 * Failing at decode gives the ordinary 400 instead. The UNKNOWN members exist because every wire
 * enum in this module has one; here they are unreachable by coercion.
 *
 * Note the response reuse: unlike `NotificationSettings` there are no optional properties, so
 * there is no absent-means-leave-unchanged case for the server to merge -- a PATCH restates the
 * whole object.
 */
@Serializable
data class PrivacySettings(
    @SerialName("show_online") val showOnline: PresenceAudience,
    @SerialName("show_last_seen") val showLastSeen: PresenceAudience,
    @SerialName("show_date_of_birth") val showDateOfBirth: PresenceAudience,
    @SerialName("show_location") val showLocation: PresenceAudience,
    @SerialName("allow_messages_from") val allowMessagesFrom: MessageAudience,
    /** False takes `profile_url` out of `UserPublic`. */
    @SerialName("allow_profile_link") val allowProfileLink: Boolean,
)

/**
 * `PrivacySettingsUpdate` is `allOf: [PrivacySettings]` and adds nothing, so the request body is
 * the same type. Named here so the `updatePrivacySettings` body is greppable by its contract
 * schema name; if a future contract version gives the update its own properties, this alias is
 * where that split starts.
 */
typealias PrivacySettingsUpdate = PrivacySettings

// -------------------------------------------------------------------------- email change

/**
 * `RequestEmailChange` -- `POST /users/me/email/change-request`, operationId `requestEmailChange`.
 *
 * The contract's name for the schema is `RequestEmailChange`, without a `Request` suffix; kept
 * verbatim so the type and the schema grep as the same thing.
 *
 * [password] is `writeOnly` in the contract: re-authentication, because an attacker on a stolen
 * session who could move the address unchallenged would own the account's password reset too.
 * The endpoint answers 202 with NO BODY, and the confirmation goes to the NEW address, so there
 * is no response type here that could echo either the password or the pending address back.
 */
@Serializable
data class RequestEmailChange(
    /** `format: email`, `maxLength: 254`. */
    @SerialName("new_email") val newEmail: String,
    /** Write-only. Never store this, never log it, never put it in a retry buffer. */
    @SerialName("password") val password: String,
)

/**
 * Body of `POST /users/me/email/confirm`, operationId `confirmEmailChange`.
 *
 * The contract declares this one inline rather than as a named schema (`required: [token]`), so
 * the name below is this module's, not the contract's. Answers 204.
 */
@Serializable
data class ConfirmEmailChangeRequest(
    /** The single-use token from the confirmation message sent to the new address. */
    @SerialName("token") val token: String,
)

// ------------------------------------------------------------------------------ blocking

/**
 * `BlockedUser` -- an entry of `listBlockedUsers`. Blocking itself has no body in either
 * direction: `blockUser`/`unblockUser` are a POST and DELETE on `/users/{user_id}/block`
 * answering 204.
 *
 * The nested profile is `UserPublic`, which has no `email` and must never gain one. This list is
 * the one screen that shows an account the user has deliberately cut off, so a server that
 * widened it to `UserPrivate` would hand out the email address of every blocked account.
 */
@Serializable
data class BlockedUser(
    @SerialName("user") val user: UserPublic,
    @SerialName("blocked_at") val blockedAt: Timestamp,
)

/** `BlockedUserPage`. Cursor-paginated like every other list in the contract. */
@Serializable
data class BlockedUserPage(
    @SerialName("items") val items: List<BlockedUser> = emptyList(),
    @SerialName("page_info") val pageInfo: PageInfo,
)

// ------------------------------------------------------------------------------ presence

/**
 * The `state` enum of `POST /presence`, operationId `updatePresence`.
 *
 * The client sends [ONLINE] on foreground and [OFFLINE] on background or a clean disconnect; an
 * unexpected disconnect keeps the user online for a 60-second grace period before the server
 * writes `last_seen_at`. Presence is reported, never inferred from request traffic -- polling in
 * the background would otherwise read as "online" to everyone.
 */
@Serializable
enum class PresenceState {
    @SerialName("online") ONLINE,
    @SerialName("offline") OFFLINE,
    @SerialName("unknown") UNKNOWN,
}

/**
 * Body of `POST /presence`. Inline in the contract (`required: [state]`), so the name is this
 * module's. Answers 204.
 *
 * [state] carries no default, so an unrecognised value fails to decode instead of coercing to
 * [PresenceState.UNKNOWN] -- an "unknown" presence state has no meaning to write down, and the
 * two ways of guessing are to leave a backgrounded user showing as online or to hide an active
 * one. A 400 tells the client its build is wrong; a guess tells nobody anything.
 */
@Serializable
data class UpdatePresenceRequest(
    @SerialName("state") val state: PresenceState,
)

// ------------------------------------------------------------------------- profile QR

/**
 * `QrCode.target_type`. Unlike the privacy and presence enums this one appears only in
 * RESPONSES, so it keeps an [UNKNOWN] default: a target kind added in a later minor version
 * should leave a shipped client showing an unrecognised code rather than failing to parse the
 * screen it is on.
 */
@Serializable
enum class QrTargetType {
    @SerialName("user") USER,
    @SerialName("public_group") PUBLIC_GROUP,
    @SerialName("private_group_invite") PRIVATE_GROUP_INVITE,
    @SerialName("unknown") UNKNOWN,
}

/**
 * `QrCode` -- the 200 body of `getMyProfileQr`, `getUserProfileQr` and `getGroupQr`. One schema
 * for all three; the variant is in [targetType].
 *
 * [payload] is a universal link built on the IMMUTABLE target id, not on a username. A printed
 * or screenshotted code outlives the username it was made from -- usernames change every 30 days
 * and the old one is only reserved for 30 more, after which a username-based code would resolve
 * to whoever picked it up next.
 *
 * For [QrTargetType.PRIVATE_GROUP_INVITE] the payload embeds an invite token, which makes the
 * image itself the credential: anyone who can see it can join. Treat it like a secret when
 * sharing, and expect it to stop working when the group rotates its invite link.
 */
@Serializable
data class QrCode(
    @SerialName("payload") val payload: String,
    @SerialName("image_url") val imageUrl: String,
    @SerialName("target_type") val targetType: QrTargetType = QrTargetType.UNKNOWN,
    @SerialName("target_id") val targetId: Uuid,
)

// ------------------------------------------------------------------- account deletion

/**
 * `AccountDeletionStatus` -- the 202 body of `requestAccountDeletion`. Cancelling
 * (`cancelAccountDeletion`, DELETE on the same path) answers 204 with no body.
 *
 * Deletion is scheduled, not immediate: [recoveryDays] is `const: 30` in the contract and
 * [scheduledFor] is when the account actually goes. Both are `required`, so neither carries a
 * default -- a client that displayed a defaulted recovery window would be telling the user a
 * deadline the server never stated, on the one screen where getting it wrong is unrecoverable.
 *
 * kotlinx does NOT enforce `const`; it decodes whatever arrives, for [status] as much as for
 * [recoveryDays]. The server is what has to hold them to `scheduled` and 30.
 */
@Serializable
data class AccountDeletionStatus(
    /** `const: scheduled` -- see [STATUS_SCHEDULED]. Modelled as a String, as the contract does. */
    @SerialName("status") val status: String,
    @SerialName("scheduled_for") val scheduledFor: Timestamp,
    @SerialName("recovery_days") val recoveryDays: Int,
) {
    companion object {
        const val STATUS_SCHEDULED = "scheduled"
        const val RECOVERY_DAYS = 30
    }
}
